import React, {useEffect, useMemo, useRef, useState} from 'react';
import Container from "../../components/Container";
import InnerPage from "../../components/InnerPage";
import SliderButton from "../../components/SliderButton";
import Modal from "../../components/Modal";
import PlayButton from "../../components/GameBlock/PlayButton";
import Error from "../404/Error";
import BG from './assets/bg_about.webp';
import ReactPlayer from "react-player";
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import Layout from "react-masonry-list";
import { useMediaQuery } from "react-responsive";
import { LG_MOBILE, LG_TABLET, MOBILE, TABLET, UPLOADHOST, WIDE } from "../../constants/constants";
import { useAbout } from "../../api/api";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import styles from "./about.module.scss";
import Page from "../../components/Page";

const About = () => {
  const [t, i18n] = useTranslation("global");
  const lang = useMemo(() => i18n.language.slice(0, 2), [i18n.language]);
  const [play, setPlay] = useState(false);
  const sliderRef = useRef(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const { aboutData, isAboutLoading, aboutError } = useAbout(lang);

  const isWide = useMediaQuery({ minDeviceWidth: LG_TABLET + 1, maxDeviceWidth: WIDE });
  const isLgTablet = useMediaQuery({ minDeviceWidth: TABLET + 1, maxDeviceWidth: LG_TABLET });
  const isTablet = useMediaQuery({ minDeviceWidth: LG_MOBILE + 1, maxDeviceWidth: TABLET });
  const isLgMobile = useMediaQuery({ minDeviceWidth: MOBILE + 1, maxDeviceWidth: LG_MOBILE });
  const isMobile = useMediaQuery({ maxDeviceWidth: MOBILE });

  useEffect(() => {
    if (aboutData?.data?.gallery?.images?.[0]?.src) {
      setSelectedImage(aboutData.data.gallery.images[0].src);
    }
  }, [aboutData?.data?.gallery?.images]);

  const handleClick = index => {
    setActiveItemIndex(index);
    sliderRef?.current?.goToSlide(index);
  };

  const onPlayClick = () => {
    setPlay(!play)
  }

  const openModal = (img) => {
    setSelectedImage(img);
    setIsOpen(true);
  }

  const closeModal = () => {
    setSelectedImage(null);
    setIsOpen(false);
  }

  const columns = isWide ? 3 :
    isLgTablet ? 3 :
      isTablet ? 3 :
        isLgMobile ? 2 :
          isMobile ? 2 : 2;

  if (isAboutLoading) {
    return <></>
  }

  if (aboutData?.error || aboutError || aboutData.error) {
    return <Error />
  }

  const data = aboutData?.data || null;
  const gallery = data?.gallery?.images ? data.gallery.images.map((item) => {
    return {
      src: UPLOADHOST+item.url,
      size: '1400-800',
      id: item.hash,
    }
  }) : null;

  const slider = [
    ...(data?.slider?.images ?? []),
    ...(data?.slider?.youtube ?? [])
  ];

  return (
    <Page>
      <Helmet>
        <title>RedBark Studio | {t("pages.about")}</title>
      </Helmet>
      <InnerPage className={styles.wrapper} background={BG}>
        <Container className={styles.page}>
          <div className="row">
            <div className="col-lg-12 text-center">
              {
                data && data.header &&
                <>
                  <h2 className='text-pink'>{ data.header.title }</h2>
                  <div className="text-large">
                    <p>{ data.header.description }</p>
                  </div>
                </>
              }
            </div>
          </div>
          { slider.length > 0 &&
            <div className={styles.mainSlider}>
              <Carousel
                ref={sliderRef}
                draggable={false}
                showDots={false}
                responsive={{ wide: { breakpoint: { max: 10000, min: 0 }, items: 1 } }}
                slidesToSlide={1}
                items={1}
                infinite={false}
                removeArrowOnDeviceType={["wide", "tablet", "mobile"]}
                containerClass={styles.mainSliderWrap}
                itemClass={styles.slideWrap}
                beforeChange={index => setActiveItemIndex(index)}
                swipeable={false}
                customTransition={'transform 0ms ease'}
              >
                {slider.map((item, index) => {
                  if (item.youtube) {
                    return (
                      <div key={index+item.hash} className={styles.mainSlide}>
                        <ReactPlayer
                          key={index + item.type}
                          controls={false}
                          playing={index === activeItemIndex}
                          playIcon={<PlayButton onClick={onPlayClick}/>}
                          width={'100%'}
                          height={'100%'}
                          url={item.youtube}
                          muted={true}
                          light={true}
                        />
                      </div>
                    )
                  } else {
                    return (
                      <div key={index+item.hash} className={styles.mainSlide}>
                        <img src={`${UPLOADHOST}${item.url}`} alt={item.hash}/>
                      </div>
                    )
                  }}
                )}
              </Carousel>
            </div>
          }
        </Container>

        { data && slider.length > 4 ?
          <div className={styles.smallSlider}>
            <Carousel
              draggable={true}
              showDots={false}
              responsive={
                {
                  wide: { breakpoint: { max: 10000, min: 1200 }, items: 5 },
                  tabletLG: { breakpoint: { max: 1200, min: 768 }, items: 4 },
                  tablet: { breakpoint: { max: 768, min: 674 }, items: 3 },
                  mobileLG: { breakpoint: { max: 674, min: 396 }, items: 3 },
                  mobile: { breakpoint: { max: 396, min: 0 }, items: 1 },
                }
              }
              slidesToSlide={1}
              centerMode={isMobile}
              infinite={true}
              arrows={true}
              customLeftArrow={<SliderButton/>}
              customRightArrow={<SliderButton direction={'next'}/>}
              itemClass={styles.smSlideWrap}
            >
              {slider.map((item, index) => {
                if (item.youtube) {
                  return (
                    <div key={index+item.id} onClick={() => handleClick(index)} className={styles.thumbnail}>
                      <ReactPlayer
                        controls={false}
                        playing={play}
                        width={'auto'}
                        height={'100%'}
                        url={item.youtube}
                        playIcon={<PlayButton onClick={onPlayClick}/>}
                        light={true}
                        muted={true}
                      />
                      <div className={styles.overlay}></div>
                    </div>
                  )
                } else {
                  return (
                    <div key={index+item.id} onClick={() => handleClick(index)} className={styles.thumbnail}>
                      <img src={UPLOADHOST+item.url} alt={`thumb-${index}`}/>
                    </div>
                  )
                }}
              )}

            </Carousel>
          </div> : (data && slider.length > 0) && (data && slider.length <= 4) ?
          <div className={styles.insteadSlider}>
            {slider.map((item, index) => {
              if (item.youtube) {
                return (
                  <div key={index+item.id} onClick={() => handleClick(index)} className={styles.thumbnail}>
                    <ReactPlayer
                      controls={false}
                      playing={play}
                      width={'auto'}
                      height={'100%'}
                      url={item.youtube}
                      playIcon={<PlayButton onClick={onPlayClick}/>}
                      light={true}
                      muted={true}
                    />
                    <div className={styles.overlay}></div>
                  </div>
                )
              } else {
                return (
                  <div key={index+item.id} onClick={() => handleClick(index)} className={styles.thumbnail}>
                    <img src={UPLOADHOST+item.url} alt={`thumb-${index}`}/>
                  </div>
                )
              }}
            )}
          </div> : <></>
        }


          <Container className={styles.gallery}>
            <div className="row">
              <div className="col-lg-12 text-center mb30">
                <h2 className='text-pink'>{data?.gallery?.title}</h2>
              </div>
            </div>
            { data && gallery &&
              <Layout
                minWidth={100}
                colCount={
                  columns
                }
                gap={isLgMobile || isMobile || isTablet ? 15 : 30}
                items={
                  gallery.map((item) => (
                    <div className={styles.galleryItem} key={item.id} onClick={() => openModal(item.src)}>
                      <img alt={item.id} src={item.src} />
                    </div>
                  ))
                }
              />
            }
          </Container>

      </InnerPage>
      {data && gallery &&
        <Modal
          isOpen={modalIsOpen}
          onClose={closeModal}
        >
          <div className={styles.modalWrap}>
            <img className={styles.modalImg} src={selectedImage} alt="gallery" />
          </div>
        </Modal>
      }
    </Page>
  )
};

export default About;